// Misc
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import pick from 'lodash/pick'
import omit from 'lodash/omit'

// Validation
import { validateRequiredString, validateMinLength, validateURL } from 'utils/validation'
import { Validator, ValidationError } from 'react-vld'

// Components
import Fade from '@material-ui/core/Fade'
import { ScrollSection } from 'atoms/ScrollSections'
import StepContainer from './StepContainer'
import StepTitle from './StepTitle'
import StepSpacer from './StepSpacer'
import StepInput from './StepInput'
import StepButton from './StepButton'
import DomainInput from './DomainInput'

export default class OnboardingUserDetailsStep extends Component {
  static propTypes = {
    formData: PropTypes.object.isRequired,
    onFormDataUpdate: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
  }

  constructor({ formData }) {
    super()
    this.state = {
      ...pick(formData, 'firstName', 'lastName', 'businessName', 'businessDomain', 'businessUrl', 'businessCategory'),
      isDomainValid: true,
      domainError: null,
    }
    this.inputs = {}
  }

  validateStep = sectionProps => (fieldValidatorError) => {
    const { sectionIndex } = sectionProps
    if (fieldValidatorError) {
      throw new ValidationError(fieldValidatorError.message, { sectionIndex })
    }
  }

  validateName = fieldName => () => {
    const { formData } = this.props
    const value = formData[fieldName]
    validateRequiredString(value)
  }

  validateBusinessDomain = () => {
    const { businessDomain, isDomainValid, domainError } = this.state
    validateRequiredString(businessDomain)
    validateMinLength(businessDomain, 5, 'Username needs to be at least 5 characters')
    if (!isDomainValid) {
      throw new ValidationError(domainError && domainError.message)
    }
  }

  validateBusinessURL = () => {
    const { formData: { businessUrl } } = this.props
    validateURL(businessUrl)
  }

  completeStep = (sectionProps, stepValidation) => () => {
    const { onSectionActivate, sectionIndex } = sectionProps
    const { validate } = stepValidation
    if (validate().isValid) {
      onSectionActivate(sectionIndex + 1)
    }
  }

  updateFormData = (fieldValidation, callback = () => {}) => () => {
    const formData = omit(this.state, 'isDomainValid', 'domainError')
    const { onFormDataUpdate } = this.props
    onFormDataUpdate(formData, () => {
      if (!fieldValidation || fieldValidation.validate().isValid) {
        callback()
      }
    })
  }

  handleFieldChange = fieldName => (ev) => {
    this.setState({ [fieldName]: ev.target.value })
  }

  handleDomainStatusChange = fieldValidation => (isDomainValid, domainError) => {
    this.setState({ isDomainValid, domainError }, fieldValidation.validate)
  }

  handleFieldEnterPress = (fieldValidation, nextFieldName) => (ev) => {
    this.updateFormData(fieldValidation, () => {
      this.inputs[nextFieldName].focus()
    })(ev)
  }

  handleBusinessCategoryEnterPress = (sectionProps, stepValidation) => (ev) => {
    this.updateFormData(null, this.completeStep(sectionProps, stepValidation))(ev)
  }

  handleFormSubmit = (ev) => {
    ev.preventDefault()
  }

  inputRef = fieldName => (input) => {
    this.inputs[fieldName] = input
  }

  renderContent = sectionProps => (stepValidation) => {
    const { isActive } = sectionProps
    const { disabled } = this.props
    const {
      firstName, lastName, businessDomain, businessName, businessUrl, businessCategory
    } = this.state
    const showNextButton = !!firstName && !!lastName && !!businessDomain && isActive
    return (
      <StepContainer>
        <form onSubmit={this.handleFormSubmit}>
          <StepTitle>Now, a bit about you</StepTitle>
          <StepSpacer />
          <Validator rule={this.validateName('firstName')}>
            { fieldValidation => (
              <StepInput
                name="fname"
                placeholder="First name"
                maxLength={35}
                disabled={disabled}
                value={firstName}
                inputRef={this.inputRef('firstName')}
                validation={fieldValidation}
                onChange={this.handleFieldChange('firstName')}
                onBlur={this.updateFormData(fieldValidation)}
                onEnterPress={this.handleFieldEnterPress(fieldValidation, 'lastName')}
              />
            ) }
          </Validator>
          <StepSpacer small />
          <Validator rule={this.validateName('lastName')}>
            { fieldValidation => (
              <StepInput
                name="lname"
                placeholder="Last name"
                maxLength={35}
                disabled={disabled}
                value={lastName}
                inputRef={this.inputRef('lastName')}
                validation={fieldValidation}
                onChange={this.handleFieldChange('lastName')}
                onBlur={this.updateFormData(fieldValidation)}
                onEnterPress={this.handleFieldEnterPress(fieldValidation, 'businessName')}
              />
            ) }
          </Validator>
          <StepSpacer small />
          <Validator rule={this.validateBusinessDomain}>
            { fieldValidation => (
              <DomainInput
                disabled={disabled}
                value={businessDomain}
                inputRef={this.inputRef('businessDomain')}
                validation={fieldValidation}
                onChange={this.handleFieldChange('businessDomain')}
                onStatusChange={this.handleDomainStatusChange(fieldValidation)}
                onBlur={this.updateFormData(fieldValidation)}
                onEnterPress={this.handleFieldEnterPress(fieldValidation, 'businessUrl')}
              />
            ) }
          </Validator>
          <StepSpacer small />
          <StepInput
            name="company"
            placeholder="Company name (optional)"
            maxLength={128}
            disabled={disabled}
            value={businessName}
            inputRef={this.inputRef('businessName')}
            onChange={this.handleFieldChange('businessName')}
            onBlur={this.updateFormData()}
            onEnterPress={this.handleFieldEnterPress(null, 'businessDomain')}
          />
          <StepSpacer small />
          <Validator rule={this.validateBusinessURL}>
            { fieldValidation => (
              <StepInput
                name="website"
                placeholder="Website (optional)"
                maxLength={512}
                disabled={disabled}
                value={businessUrl}
                inputRef={this.inputRef('businessUrl')}
                validation={fieldValidation}
                onChange={this.handleFieldChange('businessUrl')}
                onBlur={this.updateFormData(fieldValidation)}
                onEnterPress={this.handleFieldEnterPress(fieldValidation, 'businessCategory')}
              />
            ) }
          </Validator>
          <StepSpacer small />
          <StepInput
            name="industry"
            placeholder="Industry (optional)"
            maxLength={255}
            disabled={disabled}
            value={businessCategory}
            inputRef={this.inputRef('businessCategory')}
            onChange={this.handleFieldChange('businessCategory')}
            onBlur={this.updateFormData()}
            onEnterPress={this.handleBusinessCategoryEnterPress(sectionProps, stepValidation)}
          />
          <Fade in={showNextButton}>
            <div>
              <StepSpacer small />
              <StepButton
                disabled={disabled}
                onClick={this.completeStep(sectionProps, stepValidation)}
              >
                next
              </StepButton>
            </div>
          </Fade>
        </form>
      </StepContainer>
    )
  }

  render() {
    return (
      <ScrollSection {...this.props}>
        { sectionProps => (
          <Validator rule={this.validateStep(sectionProps)}>
            { this.renderContent(sectionProps) }
          </Validator>
        ) }
      </ScrollSection>
    )
  }
}
